import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import React from 'react';

interface IProps {
  children: React.ReactNode;
}

interface IState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Der Google-Übersetzer, auch der in Chrome eingebaute, markiert eine
 * übersetzte Seite mit dieser Klasse am html-Element. Andere Übersetzer fängt
 * der NotFoundError unten ab.
 */
const isPageTranslated = (): boolean => {
  const { classList } = document.documentElement;
  return (
    classList.contains('translated-ltr') || classList.contains('translated-rtl')
  );
};

/**
 * Ein NotFoundError beim Einfügen oder Entfernen von Knoten heißt, dass
 * jemand anderes das DOM verändert hat, meist eine Übersetzung oder
 * Erweiterung (#1337). Ein Neuladen allein hilft dann nicht.
 */
const shouldShowTranslationHint = (error: Error | null): boolean =>
  isPageTranslated() || error?.name === 'NotFoundError';

export class ErrorBoundary extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): IState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Unhandled UI error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Alert severity='error' sx={{ maxWidth: 560, width: '100%' }}>
            <Stack spacing={2}>
              <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>
                Es ist ein unerwarteter Fehler aufgetreten.
              </Typography>
              <Typography variant='body2'>
                Die Anwendung konnte den letzten Vorgang nicht abschließen. Bitte
                laden Sie die Seite neu. Sollte das Problem weiterhin bestehen,
                wenden Sie sich an Ihren Ansprechpartner im Notarbüro.
              </Typography>
              {shouldShowTranslationHint(this.state.error) && (
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                  Häufige Ursache ist eine automatische Übersetzung der Seite
                  durch den Browser oder eine Browser-Erweiterung. Bitte
                  schalten Sie die Übersetzung für diese Seite aus und laden
                  Sie die Seite anschließend neu.
                </Typography>
              )}
              {this.state.error?.message && (
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ wordBreak: 'break-word' }}
                >
                  {this.state.error.message}
                </Typography>
              )}
              <Box>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={this.handleReload}
                >
                  Seite neu laden
                </Button>
              </Box>
            </Stack>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}
